import { useContext, useEffect, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import { digObject, toggleArray } from '@domain-group/javascript-utils'

import { generateUrls } from '@hooks/useCampaign'

import adCopyTypes from '@functions/adCopy'
import copyToClipboard from '@functions/copyToClipboard'

import ToggleListItem from '@components/ToggleListItem'

const defaultState = {
  default: true,
  digital: true,
  editorial: true,
  newspaper: true,
  print: true,
  shareAdCopy: false,
  shareLink: '',
  sharedTypes: adCopyTypes.map(adCopyType => adCopyType.key),
  signboards: true,
}

const MODAL_KEY = 'CopyLinkModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CopyLinkModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign, link } = modalPayload

  const [state, setState] = useState(defaultState)
  const { sharedTypes, shareAdCopy, shareLink } = state

  useEffect(() => {
    const { adCopyQueryParams } = generateUrls(campaign, { adCopyTypes: sharedTypes })

    if (shareAdCopy){
      setState({
        ...state,
        shareLink: `${link + adCopyQueryParams}`,
      })
    } else {
      setState({
        ...state,
        shareLink: link,
      })
    }
  }, [shareAdCopy, sharedTypes])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Copy" titleSecondLine="Link" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>
            Link Ready
          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            You can share this link to provide public access to this campaign.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Form.Field label="Sharing Settings">
          <ToggleListItem
            onClick={() => setState({
              ...state,
              shareAdCopy: !shareAdCopy,
            })}
            toggleOnText="Yes"
            toggleOffText="No"
            toggleValue={shareAdCopy}
          >
            <ToggleListItem.Title>Send Ad Copy</ToggleListItem.Title>
          </ToggleListItem>

          {shareAdCopy && (
            <>
              {/*
               // @ts-expect-error TS2741 */}
              <Form.Field label="Ad Copy Types" marginTop="medium" />

              {shareAdCopy && adCopyTypes.map((adCopyType) => {
                const { key, title } = adCopyType
                const isChecked = state[key]

                return (
                  <ToggleListItem
                    key={key}
                    onClick={() => {
                      setState({
                        ...state,
                        [key]: !isChecked,
                        sharedTypes: toggleArray(state.sharedTypes, key),
                      })
                    }}
                    toggleOnText="Yes"
                    toggleOffText="No"
                    toggleValue={isChecked}
                  >
                    <ToggleListItem.Title>{title}</ToggleListItem.Title>
                  </ToggleListItem>
                )
              })}
            </>

          )}
        </Form.Field>

        <Form.Field direction="column" label="Copy Link">
          <Box>
            <input readOnly value={shareLink} />

            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faCopy} />}
              // @ts-expect-error TS2322
              marginLeft="medium"
              onClick={() => copyToClipboard(shareLink)}
              size="medium"
              width="auto"
            />
          </Box>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CopyLinkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
